/* eslint-disable sonarjs/cognitive-complexity */
import { useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Button, { Color, Size } from 'lib/Button';
import downloadSvg from 'assets/icons/download.svg';
import Preloader from 'lib/Preloader';
import { cancelFunctions } from 'utils/cancelFunctions';
import { isDirectSalesCampaignCloned } from 'components/pages/Planner/hooks/utils/isDirectSalesCampaignCloned';
import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import { Store } from 'components/common/types/Store.types';
import {
  AllocationReportProps,
  Frame,
  ReportType,
  SelectedLine,
} from 'components/common/AllocationReport/AllocationReport.types';
import LoadingOverlayComponent from 'lib/Table/LoadingOverlayComponent';
import Checkbox from 'lib/Checkbox';
import { fetchCampaignFrameListReport, fetchCampaignLinesInfo } from 'modules/api/directSalesCampaign';
import { formatCurrency, formatNumber } from 'modules/I18N';
import { HeaderBackgroundColor, TableHeader, TextColor } from 'components/common/TableHeader';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getMarketId } from 'store/publisher/selectors';
import { DropdownDealLine } from '../Filters/DropdownDealLine';
import { DirectSalesFramesReportTypeButtons } from '../ReportTypeButtons/DirectSalesFramesReportTypeButtons';
import { transformFrameListReport, transformSelectedLinesToReport } from '../utils';
import { AggregatedFrame, TransformedFramesReport } from './FramesReport.types';
import { formatAddress, formatBooleanAsZeroOne, FrameReportColumn, getVisibleFrameReportColumns } from './columns';
import { useExportFramesReport } from '../useExportFramesReport';

export interface FramesReportTableParams {
  gridOptions: GridOptions;
  onGridReady: (event: GridReadyEvent<Frame[]>) => void;
  rowData: Frame[];
}

const gridOptions = {
  domLayout: 'autoHeight' as const,
  suppressMovableColumns: true,
  suppressContextMenu: true,
  defaultColDef: {
    resizable: true,
    sortable: true,
    suppressMenu: true,
    minWidth: 100,
    flex: 1,
  },
  pinnedBottomRowData: [{ frameId: 'Total', impression: 0, price: 0, indexedPrice: 0 }],
};

const defaultColDef: ColDef = {
  headerComponent: TableHeader,
  headerComponentParams: {
    backgroundColor: HeaderBackgroundColor.WHITE,
    sortable: true,
    textColor: TextColor.SECONDARY,
  },
};

export const FramesReport: React.FC<Omit<AllocationReportProps, 'isDirectSales'>> = ({
  dealLines,
  defaultSelectedLine,
  changeReportSwitch,
}) => {
  const campaignId = useSelector(getDealId);
  const { campaignId: originalCampaignId } = useParams();
  const marketId = useSelector(getMarketId);
  const { localeCode, currencyCode } = useSelector((state: Store) => state.publisher.configuration);
  const gridRef = useRef(null);

  const frameListConfiguration = useSelector((state: Store) => state.publisher.frameListConfiguration);

  const [originalDealLines, setOriginalDealLines] = useState<SelectedLine[]>([]);
  const [selectedLines, setSelectedLines] = useState<SelectedLine[]>([defaultSelectedLine]);
  const [activeReportType, setActiveReportType] = useState(ReportType.NOT_SAVED);
  const [allLinesSelected, setAllLinesSelected] = useState<boolean>(false);
  const [lineReports, setLineReports] = useState<TransformedFramesReport>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowData, setRowData] = useState<AggregatedFrame[]>([]);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const visibleColumns = useMemo(() => getVisibleFrameReportColumns(frameListConfiguration), [frameListConfiguration]);
  const { handleExportFramesReport } = useExportFramesReport({ rowData, columns: visibleColumns });

  const isCampaignCloned = isDirectSalesCampaignCloned(campaignId);

  const filteredCancelledLines = dealLines.filter(({ lineStatus }) => lineStatus !== DirectSalesStatusOption.CANCELLED);

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const buildColDef = (col: FrameReportColumn): ColDef<AggregatedFrame> => {
    const def: ColDef<AggregatedFrame> = {
      headerComponentParams: { label: col.label },
      field: col.field,
    };

    if (col.pinned === 'left') {
      def.pinned = 'left';
      def.minWidth = 25;
      def.resizable = true;
    } else if (col.pinned === 'right') {
      def.pinned = 'right';
      def.resizable = false;
      def.minWidth = 40;
    }

    if (col.format === 'number') {
      def.enableRowGroup = true;
      def.valueFormatter = (params) => formatNumber({ value: params.value, localeCode });
    } else if (col.format === 'currency') {
      def.valueFormatter = (params) => formatCurrency({ value: params.value, currencyCode, localeCode });
    } else if (col.format === 'tags') {
      def.valueFormatter = (params) => (Array.isArray(params.value) ? params.value.join(', ') : (params.value ?? ''));
    } else if (col.format === 'address') {
      def.valueFormatter = (params) => formatAddress(params.value);
    } else if (col.format === 'boolean') {
      def.valueFormatter = (params) => String(formatBooleanAsZeroOne(params.value));
    }

    return def;
  };

  const columnDefs: ColDef<AggregatedFrame>[] = useMemo(
    () => visibleColumns.map(buildColDef),
    [localeCode, currencyCode, visibleColumns],
  );

  const fetchFrameListReport = async (lineIds: string[]): Promise<void> => {
    setIsLoading(true);

    try {
      const data = await fetchCampaignFrameListReport(marketId, campaignId, cancelFunctions, lineIds);

      setLineReports(transformFrameListReport(data));
      // eslint-disable-next-line no-empty
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeReportType === ReportType.NOT_SAVED || originalDealLines.length > 0) return;

    const fetchOriginalCampaignAndReport = async (): Promise<void> => {
      try {
        const data = await fetchCampaignLinesInfo(marketId, originalCampaignId ?? campaignId, cancelFunctions);

        const originalLines = data.dealLines
          .filter((line) => line.state.status !== DirectSalesStatusOption.CANCELLED)
          .map<SelectedLine>((line) => ({
            lineDefaultReportType: ReportType.SAVED,
            lineId: line.id,
            lineName: line.name,
            lineStatus: line.state.status,
          }));

        setOriginalDealLines(originalLines);

        fetchFrameListReport(dealLines.map(({ lineId }) => lineId));
        // eslint-disable-next-line no-empty
      } catch {}
    };
    fetchOriginalCampaignAndReport();
  }, [marketId, originalCampaignId, activeReportType]);

  useEffect(() => {
    if (activeReportType === ReportType.SAVED) return;

    const allDealLineIds = dealLines.map(({ lineId }) => lineId);

    fetchFrameListReport(allDealLineIds);
  }, [marketId, campaignId, activeReportType]);

  useEffect(() => {
    if (!gridApi) return;

    const selectedDealLines = selectedLines.map(({ lineId }) => lineId);
    const { reportLines, totalImpressions, totalPrice, totalIndexedPrice } = transformSelectedLinesToReport(
      selectedDealLines,
      lineReports,
    );

    gridApi.setPinnedBottomRowData([
      { frameId: 'Total', impression: totalImpressions, price: totalPrice, indexedPrice: totalIndexedPrice },
    ]);
    setRowData(reportLines);
  }, [gridApi, lineReports.length, selectedLines]);

  useEffect(() => {
    if (!originalDealLines.length) return;

    setSelectedLines([activeReportType === ReportType.SAVED ? originalDealLines[0] : defaultSelectedLine]);
  }, [activeReportType, originalDealLines]);

  const toggleSelectedAll = ({
    currentSelectedLines,
    toggledValue,
  }: {
    currentSelectedLines?: SelectedLine[];
    toggledValue?: boolean;
  }): void => {
    if (toggledValue !== undefined) {
      return setAllLinesSelected(toggledValue);
    }

    return setAllLinesSelected(currentSelectedLines?.length === filteredCancelledLines.length);
  };

  return (
    <div className="h-full flex flex-col bg-white" data-test-id="frames-report">
      <header className="flex flex-col gap-3 p-4 border-b border-neutral-950-opacity-10 mb-2">
        <DirectSalesFramesReportTypeButtons
          activeReportType={activeReportType}
          setActiveReportType={setActiveReportType}
          isDisabled={!isCampaignCloned}
        />
        <div className="flex flex-col flex-1 items-center gap-4 sm:flex-row sm:items-end sm:gap-3">
          {changeReportSwitch}
          <DropdownDealLine
            dealLines={activeReportType === ReportType.SAVED ? originalDealLines : dealLines}
            handleChangeLine={(_, newSelection) => {
              const lines = (Array.isArray(newSelection) ? newSelection : [newSelection]) as SelectedLine[];
              setSelectedLines(lines);
              toggleSelectedAll({ currentSelectedLines: lines });
            }}
            selectedLines={selectedLines}
            isLineStatusShown
            isMultiSelectLine
            resetSelectedLines={() => {
              toggleSelectedAll({ toggledValue: false });
              setSelectedLines(ReportType.NOT_SAVED ? [defaultSelectedLine] : [originalDealLines[0]]);
            }}
          />
          <div
            role="presentation"
            className="flex items-center gap-2 self-center pt-6"
            onClick={() => {
              toggleSelectedAll({ toggledValue: !allLinesSelected });

              if (!allLinesSelected === true) {
                setSelectedLines(
                  activeReportType === ReportType.NOT_SAVED ? filteredCancelledLines : originalDealLines,
                );
              } else {
                setSelectedLines(ReportType.NOT_SAVED ? [defaultSelectedLine] : [originalDealLines[0]]);
              }
            }}
          >
            <Checkbox isSelected={allLinesSelected} />
            <p className="body-base font-medium select-none">Select all</p>
          </div>
          <Button
            classNames="ml-auto"
            svg={downloadSvg}
            label="Export report"
            size={Size.LARGE}
            color={Color.TRANSPARENT}
            onClick={handleExportFramesReport}
          />
        </div>
      </header>
      {isLoading ? (
        <Preloader isLoading />
      ) : (
        <div className="ag-theme-material allocation-report-table">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            rowData={rowData}
            rowHeight={36}
            loadingOverlayComponent={LoadingOverlayComponent}
          />
        </div>
      )}
    </div>
  );
};
/* eslint-enable sonarjs/cognitive-complexity */
